import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { PersonalizationFirstPersonStatus, PersonalizationPerson, ProductState, ShoppingCartService } from '@platform-frontend/checkout';
import { TypedChanges } from '@platform-frontend/util';
import { combineLatest, Observable, ReplaySubject, Subject, take, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'yat-selected-person-card',
  templateUrl: './selected-person-card.component.html',
  styleUrls: ['./selected-person-card.component.scss'],
  providers: [ShoppingCartService]
})
export class SelectedPersonCardComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  constructor(private cdr: ChangeDetectorRef, private shoppingCartService: ShoppingCartService) {}

  @Input()
  persons: PersonalizationPerson<string | null>[] = [];

  @Input()
  isMinified: boolean = true;

  @Input()
  showIcon: boolean = true;

  @Output()
  cardSelect = new EventEmitter<void>();

  @ViewChild('namesText', { static: false })
  namesElement: ElementRef | undefined;

  @ViewChild('container', { static: false })
  containerElement: ElementRef | undefined;

  @Select(ProductState.maxNumberOfPersons)
  maxNumberOfPersons$: Observable<number>;

  isTextOverflowing: boolean = false;
  scrollDistance: string = '0px';
  personsChanged$: Subject<void> = new ReplaySubject<void>(1);
  personalizationAdditionStatus$: Observable<PersonalizationFirstPersonStatus>;

  private mutationObserver: MutationObserver;

  ngOnInit(): void {
    this.personalizationAdditionStatus$ = this.shoppingCartService.getPersonalizationAdditionStatus();
  }

  ngAfterViewInit(): void {
    const namesElementRendered$ = new Subject<void>();

    this.mutationObserver = new MutationObserver(() => {
      if (this.namesElement) {
        namesElementRendered$.next();
      }
    });

    this.mutationObserver.observe(this.containerElement?.nativeElement, {
      childList: true,
      subtree: true
    });

    combineLatest([this.personsChanged$, namesElementRendered$.pipe(take(1))])
      .pipe(
        tap(() => this.checkOverflow()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.mutationObserver.disconnect();
  }

  ngOnChanges(changes: TypedChanges<SelectedPersonCardComponent>): void {
    if (changes.persons) {
      this.cdr.detectChanges();
      this.checkOverflow();
      this.personsChanged$.next();
    }
  }

  onCardSelect(): void {
    this.cardSelect.emit();
  }

  get areNamesToBeDisplayed(): boolean {
    return this.persons.filter(person => person.fullName !== null).length > 0;
  }

  private checkOverflow(): void {
    const element = this.namesElement?.nativeElement;
    const container = this.containerElement?.nativeElement;

    if (!element || !container) return;

    this.isTextOverflowing = element.scrollWidth > container.clientWidth;

    if (this.isTextOverflowing) {
      const overflow = element.scrollWidth - container.clientWidth;
      this.scrollDistance = `${overflow}px`;
    }
  }
}
